import React, { useState } from "react";
import { Modal, Form, Button, Alert, Spinner, Col, Row } from "react-bootstrap";
import { getErrorDetail } from "../../../utility/utils.js";
import { ReactComponent as OutlineCross } from "assets/svg/outline-cross.svg";
import { ReactComponent as PlaneIcon } from "assets/svg/plane-icon.svg";
import { ReactComponent as PlaneDropIcon } from "assets/svg/plane-drop-icon.svg";
import { fetchOrchidAPI } from "utils/api";
import IconButton from "components/icon-button";
import ResponsiveModal from "components/responsive-modal";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import getClasses from "utils/getClasses";
import styles from "./styles.module.scss";

const InviteModalNext = ({ show, onHide, invitee }) => {
  const { isPhonePortrait } = usePhonePortraitContext();
  const [state, setState] = useState({
    email: "",
    first_name: "",
    last_name: "",
    description: "",
    inviteSent: false,
    busy: false,
    error: "",
    validated: false,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setState({ ...state, inviteSent: false, validated: true });
      return;
    }

    setState({ ...state, busy: true });

    const response = await fetchOrchidAPI("/api/users/v1/invites/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invitee_email: state.email,
        invite_text: state.description,
        first_name: state.first_name,
        last_name: state.last_name,
        invite_as_professional: invitee === "orchid_pro",
      }),
    });
    if (!response.ok) {
      setState({
        ...state,
        error: `${getErrorDetail(await response.text())}`,
        busy: false,
      });
      return;
    }
    setState({ ...state, inviteSent: true, busy: false });
  };

  const onInputChange = (event) => {
    setState({
      ...state,
      [event.target.id]: event.target.value,
    });
  };

  return (
    <ResponsiveModal
      show={show}
      onShow={() =>
        setState({ ...state, inviteSent: false, error: "", validated: false })
      }
      onHide={onHide}
      centered
      className={styles["invite-others-modal"]}
    >
      {state.inviteSent || state.error ? (
        <>
          <IconButton
            onClick={onHide}
            className={styles["custom-modal-close-button"]}
          >
            <OutlineCross />
          </IconButton>
          <div className={styles["invite-confirm"]}>
            {state.error ? (
              <div className="m-auto text-center">
                <PlaneDropIcon />
                <h2 className="mt-3 deep-blue questrial">Could not send</h2>
                <Alert variant="danger">{state.error.toString()}</Alert>
                <p className="mb-0">Please try again.</p>
              </div>
            ) : (
              <div className="m-auto text-center">
                <PlaneIcon />
                <h2 className="mt-3 deep-blue questrial">Sent!</h2>
                <p className="mb-0">
                  {invitee === "orchid_pro"
                    ? "Your referrel was sent successfully."
                    : "Your invite was sent successfully."}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <Modal.Header
            className={getClasses(
              styles["invite-others-modal__header"],
              isPhonePortrait && styles["invite-others-modal__header--phone"],
            )}
            closeButton={false}
          >
            <Modal.Title className="deep-blue questrial">
              {invitee === "orchid_pro"
                ? "Refer a professional"
                : "Invite others to Orchid"}
            </Modal.Title>
            <IconButton onClick={onHide} className={"ml-3"}>
              <OutlineCross />
            </IconButton>
          </Modal.Header>
          <Modal.Body
            className={
              !isPhonePortrait
                ? styles["invite-others-modal__body"]
                : styles["invite-others-modal__body--phone"]
            }
          >
            <Form
              onSubmit={handleSubmit}
              validated={state.validated}
              noValidate
              id="invite-others-form"
            >
              <Form.Group controlId="email">
                <Form.Label className="rg-12 gray01">
                  Email <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  type="email"
                  placeholder="Enter email"
                  value={state.email}
                  onChange={onInputChange}
                />
              </Form.Group>
              <Row>
                <Form.Group as={Col} md="6" controlId="first_name">
                  <Form.Label className="rg-12 gray01">
                    Invitee First Name <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Enter First Name"
                    value={state.first_name}
                    onChange={onInputChange}
                  />
                </Form.Group>
                <Form.Group as={Col} md="6" controlId="last_name">
                  <Form.Label className="rg-12 gray01">
                    Invitee Last Name <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    required
                    type="text"
                    placeholder="Enter Last Name"
                    value={state.last_name}
                    onChange={onInputChange}
                  />
                </Form.Group>
              </Row>
              <Form.Group controlId="description">
                <Form.Label className="rg-12">
                  Description &nbsp;
                  <span>({state.description?.length || "0"}/500)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  placeholder="Let others know why they should join!"
                  rows={5}
                  value={state.description}
                  onChange={onInputChange}
                  maxLength={500}
                />
                {/* <Form.Control.Feedback type="invalid">
                      {state.description.length >= 500 && `You have exceeded the maximum number of character limit.`}
                    </Form.Control.Feedback> */}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer
            className={
              !isPhonePortrait
                ? styles["invite-others-modal__footer"]
                : styles["invite-others-modal__footer--phone"]
            }
          >
            <Button
              form="invite-others-form"
              className={!isPhonePortrait ? "p-btn" : "w-100"}
              variant="primary"
              type="submit"
              disabled={state.busy}
            >
              Invite{" "}
              {state.busy && (
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </Button>
          </Modal.Footer>
        </>
      )}
    </ResponsiveModal>
  );
};

export default InviteModalNext;
